'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { V2Layout, RevealSection, useV2Lang, Eyebrow, Rule, ACCENT, HAIRLINE, formatDate, venueShort } from '../../_layout';
import { appLanguageToContentLocale } from '@/i18n/contentLocale';
import { resolveEventParam } from '../../eventSlugs';

type Heading = { id: string; level: 2 | 3; text: string };
type Section = { h2: Heading; subs: Heading[] };

function extractHeadings(md: string): Heading[] {
  const out: Heading[] = [];
  let h2idx = 0, h3idx = 0;
  for (const line of md.split('\n')) {
    const m = line.match(/^(##{1,2})\s+(.+?)\s*$/);
    if (!m) continue;
    const level = m[1].length === 2 ? 2 : 3;
    const text = m[2].replace(/[*_`]/g, '').trim();
    const id = level === 2 ? `h2-${h2idx++}` : `h3-${h3idx++}`;
    out.push({ id, level, text });
  }
  return out;
}

function groupHeadings(headings: Heading[]): Section[] {
  const sections: Section[] = [];
  for (const h of headings) {
    if (h.level === 2) sections.push({ h2: h, subs: [] });
    else if (sections.length > 0) sections[sections.length - 1].subs.push(h);
  }
  return sections;
}

function jumpTo(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  }
}

function EventTOC({
  headings,
  label,
  pinLabel,
}: {
  headings: Heading[];
  label: string;
  pinLabel: string;
}) {
  const sections = useMemo(() => groupHeadings(headings), [headings]);
  const [active, setActive] = useState<string>('');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(sections.filter((s) => s.subs.length > 0).map((s) => s.h2.id))
  );
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const hoverTimer = useRef<number | null>(null);
  const open = hovered || pinned;
  useEffect(() => () => { if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current); }, []);

  // Scroll-tracking: active = the last heading whose top has scrolled above
  // the trigger line near the top of the viewport. More reliable than
  // IntersectionObserver because it always has a definite answer at every
  // scroll position (no "active zone" to fall outside of).
  useEffect(() => {
    if (headings.length === 0) return;
    const TRIGGER_Y = 140; // px from viewport top
    let rafId = 0;
    const update = () => {
      rafId = 0;
      let activeId = '';
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= TRIGGER_Y) {
          activeId = h.id;
        } else {
          break;
        }
      }
      setActive(activeId);
    };
    const onScroll = () => {
      if (rafId !== 0) return;
      rafId = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
    };
  }, [headings]);

  // Auto-expand the section containing the active heading
  useEffect(() => {
    if (!active) return;
    const parent = sections.find(
      (s) => s.h2.id === active || s.subs.some((sub) => sub.id === active)
    );
    if (parent && parent.subs.length > 0 && !expanded.has(parent.h2.id)) {
      setExpanded((prev) => new Set([...prev, parent.h2.id]));
    }
  }, [active, sections, expanded]);

  const toggleSection = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (headings.length === 0) return null;

  const enter = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHovered(true), 120);
  };
  const leave = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    setHovered(false);
  };
  const activeSection =
    sections.find((s) => s.h2.id === active || s.subs.some((x) => x.id === active))?.h2.id ?? '';

  return (
    <div className="sticky top-24" onMouseEnter={enter} onMouseLeave={leave}>
      {/* Collapsed rail — chapter label + section dots + arrow. Always occupies
          the 44px gutter so the article column never reflows. */}
      <div
        onClick={() => setHovered(true)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          cursor: 'pointer',
          padding: '8px 0',
          color: 'rgba(255,255,255,0.55)',
          opacity: open ? 0 : 1,
          pointerEvents: open ? 'none' : 'auto',
          transition: 'opacity 180ms ease',
        }}
      >
        <span
          className="font-mono"
          style={{
            fontSize: '10px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: '11px', alignItems: 'center' }}>
          {sections.map((s) => {
            const on = s.h2.id === activeSection;
            return (
              <span
                key={s.h2.id}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: on ? ACCENT : 'rgba(255,255,255,0.25)',
                  transform: on ? 'scale(1.35)' : 'none',
                  transition: 'all 160ms ease',
                }}
              />
            );
          })}
        </span>
        <span className="font-mono" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>→</span>
      </div>

      {/* Expanded panel — floats over the article on hover/pin, no reflow. */}
      <nav
        style={{
          position: 'absolute',
          top: '-12px',
          left: '-12px',
          width: '236px',
          maxHeight: 'calc(100vh - 7rem)',
          overflowY: 'auto',
          background: '#1f1c19',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '10px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
          padding: '18px 16px',
          opacity: open ? 1 : 0,
          transform: open ? 'none' : 'translateX(-8px)',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 180ms ease, transform 180ms ease',
          zIndex: 40,
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">
            {label}
          </span>
          <button
            type="button"
            onClick={() => setPinned((p) => !p)}
            aria-pressed={pinned}
            className="font-mono text-[10px] tracking-[0.05em] rounded-md px-2 py-[3px] transition-colors"
            style={{
              border: `1px solid ${pinned ? ACCENT : 'rgba(255,255,255,0.18)'}`,
              background: pinned ? ACCENT : 'transparent',
              color: pinned ? '#fff' : 'rgba(255,255,255,0.5)',
            }}
          >
            {pinLabel}
          </button>
        </div>
      <ul className="space-y-0">
        {sections.map((s) => {
          const isOpen = expanded.has(s.h2.id);
          const hasSubs = s.subs.length > 0;
          const h2Active = active === s.h2.id;
          const childActive = s.subs.some((x) => x.id === active);
          return (
            <li key={s.h2.id} className="border-b border-white/[0.06] last:border-b-0">
              <div className="flex items-baseline gap-1.5 py-3">
                <button
                  type="button"
                  onClick={() => hasSubs && toggleSection(s.h2.id)}
                  aria-label={hasSubs ? (isOpen ? 'Collapse section' : 'Expand section') : undefined}
                  className={`flex-none w-4 text-left font-mono text-[10px] leading-none transition-colors ${
                    hasSubs ? 'text-white/45 hover:text-white cursor-pointer' : 'text-transparent cursor-default'
                  }`}
                  tabIndex={hasSubs ? 0 : -1}
                >
                  {hasSubs ? (isOpen ? '−' : '+') : '·'}
                </button>
                <a
                  href={`#${s.h2.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    jumpTo(s.h2.id);
                  }}
                  className="block flex-1 text-[13px] leading-[1.35] transition-colors"
                  style={{
                    color: h2Active || childActive ? ACCENT : 'rgba(255,255,255,0.78)',
                    fontWeight: h2Active ? 600 : 500,
                    letterSpacing: '0.005em',
                  }}
                >
                  {s.h2.text}
                </a>
              </div>
              {hasSubs && (
                <ul
                  className="overflow-hidden transition-[max-height] duration-300 ease-out"
                  style={{
                    maxHeight: isOpen ? `${s.subs.length * 64}px` : '0px',
                    paddingLeft: '22px',
                    marginBottom: isOpen ? '8px' : '0',
                  }}
                >
                  {s.subs.map((sub) => {
                    const subActive = active === sub.id;
                    return (
                      <li key={sub.id} className="py-1">
                        <a
                          href={`#${sub.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            jumpTo(sub.id);
                          }}
                          className="block text-[12px] leading-[1.4] transition-colors"
                          style={{
                            color: subActive ? ACCENT : 'rgba(255,255,255,0.42)',
                            fontWeight: subActive ? 500 : 400,
                            paddingLeft: subActive ? '8px' : '0',
                            borderLeft: subActive ? `2px solid ${ACCENT}` : '2px solid transparent',
                            transition: 'all 160ms ease',
                          }}
                        >
                          {sub.text}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
        </ul>
      </nav>
    </div>
  );
}

type Slot = {
  start_time: string;
  end_time: string;
  location?: string;
  registration_link?: string;
  organizer?: string;
  doors_open?: string;
  show_starts?: string;
  isExpired?: boolean;
};
type Contact = { [key: string]: { type: string; value: string; label?: string; icon?: string } };
type Activity = {
  id: number;
  type: string;
  title: string;
  description: string;
  location: string;
  slots: Slot[];
  contact?: Contact;
};

const COPY = {
  CN: { back: '← 返回所有活动', dates: '日期', venues: '场地', tickets: '购票', noTickets: '——', loading: '加载中…', past: '已完成', upcoming: '即将举行', live: '正在进行', share: '分享', alsoSee: '相关阅读' },
  EN: { back: '← All events', dates: 'Dates', venues: 'Venues', tickets: 'Tickets', noTickets: '—', loading: 'Loading…', past: 'Past', upcoming: 'Upcoming', live: 'Now', share: 'Share', alsoSee: 'See also' },
  DE: { back: '← Alle Events', dates: 'Daten', venues: 'Spielstätten', tickets: 'Tickets', noTickets: '—', loading: 'Lädt…', past: 'Vergangen', upcoming: 'Kommend', live: 'Aktuell', share: 'Teilen', alsoSee: 'Siehe auch' },
};

export default function V2EventDetail() {
  const lang = useV2Lang();
  const t = COPY[lang];
  const params = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!params.id) return;
    const resolved = resolveEventParam(params.id);
    if (resolved === undefined) {
      navigate('/v2/events');
      return;
    }
    setLoading(true);
    const apiLang = appLanguageToContentLocale(lang);
    fetch(`/api/activities/${resolved}?language=${apiLang}`)
      .then((r) => {
        if (r.status === 404) {
          navigate('/v2/events');
          return null;
        }
        return r.json();
      })
      .then((data) => setEvent(data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [params.id, lang, navigate]);

  if (loading) {
    return (
      <V2Layout>
        <RevealSection className="min-h-[60vh] flex items-center justify-center">
          <div className="text-white/55 text-sm">{t.loading}</div>
        </RevealSection>
      </V2Layout>
    );
  }
  if (!event) {
    return (
      <V2Layout>
        <RevealSection className="min-h-[60vh] flex items-center justify-center">
          <div className="text-white/55 text-sm">404</div>
        </RevealSection>
      </V2Layout>
    );
  }

  const now = Date.now();
  const firstSlot = event.slots[0];
  const lastSlot = event.slots[event.slots.length - 1];
  const startTime = firstSlot ? new Date(firstSlot.start_time.replace(' ', 'T')).getTime() : 0;
  const endTime = lastSlot ? new Date(lastSlot.end_time.replace(' ', 'T')).getTime() : 0;
  const isLive = startTime <= now && now <= endTime;
  const isPast = endTime < now;
  const status = isLive ? t.live : isPast ? t.past : t.upcoming;
  const statusColor = isLive ? ACCENT : isPast ? 'rgba(255,255,255,0.5)' : ACCENT;

  const f = firstSlot ? formatDate(firstSlot.start_time, lang) : null;
  const fLast = lastSlot && lastSlot !== firstSlot ? formatDate(lastSlot.start_time, lang) : null;

  return (
    <V2Layout>
      {/* Header */}
      <RevealSection className="px-6 md:px-10 pt-32 pb-16">
        <div className="mx-auto max-w-[1400px]">
          <Link to="/events" className="inline-block text-[13px] font-mono uppercase tracking-[0.15em] text-white/50 hover:text-white mb-12">
            {t.back}
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-block h-[1px] w-6" style={{ backgroundColor: statusColor }} aria-hidden />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase" style={{ color: statusColor }}>
              {status}
            </span>
            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/55">·</span>
            <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/55">
              {event.type === 'music' ? '♪ MUSIC' : event.type === 'poetry' ? '✎ POETRY' : event.type}
            </span>
            {f && (
              <>
                <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/55">·</span>
                <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/75">
                  {f.year} · {f.month} {f.day}
                  {fLast && fLast.day !== f.day ? ` — ${fLast.month} ${fLast.day}` : ''}
                </span>
              </>
            )}
          </div>

          <h1 className="text-[clamp(40px,7vw,108px)] leading-[0.95] tracking-[-0.035em] font-bold max-w-[20ch]">
            {event.title}
          </h1>

          <div className="mt-12 grid gap-8 md:grid-cols-12 text-[13px] font-mono leading-[1.65]">
            <div className="md:col-span-3">
              <p className="uppercase tracking-[0.15em] text-white/55 mb-2">{t.dates}</p>
              {event.slots.map((s, i) => (
                <p key={i} className="text-white/85">
                  {formatDate(s.start_time, lang).month} {formatDate(s.start_time, lang).day}
                  {s.show_starts && <span className="text-white/55"> · {s.show_starts}</span>}
                </p>
              ))}
            </div>
            <div className="md:col-span-5">
              <p className="uppercase tracking-[0.15em] text-white/55 mb-2">{t.venues}</p>
              {event.slots.map((s, i) => (
                <p key={i} className="text-white/85">
                  {s.location ? venueShort(s.location) : event.location}
                </p>
              ))}
            </div>
            <div className="md:col-span-4">
              <p className="uppercase tracking-[0.15em] text-white/55 mb-2">{t.tickets}</p>
              {event.slots.map((s, i) => (
                <p key={i}>
                  {s.registration_link ? (
                    <a href={s.registration_link} target="_blank" rel="noreferrer" className="hover:no-underline underline underline-offset-4" style={{ color: ACCENT }}>
                      → {hostname(s.registration_link)}
                    </a>
                  ) : (
                    <span className="text-white/55">{t.noTickets}</span>
                  )}
                </p>
              ))}
            </div>
          </div>
        </div>
      </RevealSection>

      <Rule />

      {(() => {
        const m = event.description.match(/^\s*!\[([^\]]*)\]\(([^)]+)\)\s*\n+/);
        if (!m) return null;
        return (
          <RevealSection className="w-full bg-black flex items-center justify-center py-12 md:py-20">
            <img src={m[2]} alt={m[1]} className="block max-h-[85vh] w-auto h-auto" />
          </RevealSection>
        );
      })()}

      {/* Body / description as markdown — with sticky TOC at lg+ */}
      {(() => {
        const bodyMd = event.description.replace(/^\s*!\[[^\]]*\]\([^)]+\)\s*\n+/, '');
        const headings = extractHeadings(bodyMd);
        const tocLabel = lang === 'CN' ? '章节' : lang === 'DE' ? 'Inhalt' : 'Contents';
        const tocPin = lang === 'CN' ? '钉住' : lang === 'DE' ? 'Anheften' : 'Pin';
        const counters = { 2: 0, 3: 0 };
        const nextId = (level: 2 | 3): string => {
          const id = `${level === 2 ? 'h2' : 'h3'}-${counters[level]}`;
          counters[level] += 1;
          return id;
        };
        return (
          <article className="px-6 md:px-10 py-16 md:py-24">
            <div
              className="mx-auto max-w-[1180px] lg:grid"
              style={{
                gridTemplateColumns: '44px minmax(0, 1fr)',
                columnGap: '2.5rem',
              }}
            >
              <aside className="hidden lg:block">
                <EventTOC headings={headings} label={tocLabel} pinLabel={tocPin} />
              </aside>
              <div className="v2-prose max-w-[780px] mx-auto lg:mx-0">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({ children }) => (
                      <h2 id={nextId(2)} style={{ scrollMarginTop: '96px' }}>
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 id={nextId(3)} style={{ scrollMarginTop: '96px' }}>
                        {children}
                      </h3>
                    ),
                    img: ({ src = '', alt }) => {
                      if (src.includes('/logo-')) {
                        return <img src={src} alt={alt} style={{ display: 'inline-block', height: 'clamp(88px, 10vw, 128px)', width: 'auto', maxWidth: 'none', margin: '0.75rem 1rem', verticalAlign: 'middle', objectFit: 'contain', backgroundColor: 'rgba(255,255,255,0.95)', padding: '12px 18px', borderRadius: '8px' }} />;
                      }
                      if (src.includes('/poster-')) {
                        return <img src={src} alt={alt} style={{ display: 'block', margin: '2.5rem auto', width: '100%', maxWidth: '100%', height: 'auto' }} />;
                      }
                      // Designated recap photos: cinematic single-image moments,
                      // break out of the prose text column to feature width.
                      const isRecap = src.includes('/recap-');
                      return (
                        <img
                          src={src}
                          alt={alt}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            maxWidth: '100%',
                            maxHeight: isRecap ? '72vh' : '60vh',
                            margin: '0 auto',
                            objectFit: 'contain',
                          }}
                        />
                      );
                    },
                    p: ({ node, children, ...rest }) => {
                      const childArr = Array.isArray(children) ? children : [children];
                      const onlyImages = childArr.length > 0 && childArr.every((c: unknown) => {
                        if (typeof c === 'string') return c.trim() === '';
                        if (c && typeof c === 'object' && 'props' in c) {
                          const props = (c as { props?: { src?: string } }).props;
                          return props && typeof props.src === 'string';
                        }
                        return false;
                      });
                      if (onlyImages) {
                        // figure out whether this is a "recap" image — if so,
                        // give it a wider canvas that breaks out of the text column.
                        const firstImg = childArr.find(
                          (c) =>
                            c &&
                            typeof c === 'object' &&
                            'props' in c &&
                            typeof (c as { props?: { src?: string } }).props?.src === 'string'
                        ) as { props?: { src?: string } } | undefined;
                        const isRecap = firstImg?.props?.src?.includes('/recap-') ?? false;
                        return (
                          <p
                            {...rest}
                            className="event-figure"
                            data-recap={isRecap ? 'true' : undefined}
                            style={{
                              margin: '4rem 0',
                              textAlign: 'center',
                              maxWidth: 'none',
                            }}
                          >
                            {children}
                          </p>
                        );
                      }
                      return <p {...rest}>{children}</p>;
                    },
                  }}
                >{bodyMd}</ReactMarkdown>
              </div>
            </div>
          </article>
        );
      })()}

      <Rule />

      {/* Slots full detail */}
      <RevealSection className="px-6 md:px-10 py-16 md:py-24">
        <div className="mx-auto max-w-[1400px]">
          <Eyebrow>{t.dates} · {t.venues}</Eyebrow>
          <div className="mt-8 border-t" style={{ borderColor: HAIRLINE }}>
            {event.slots.map((s, i) => {
              const fs = formatDate(s.start_time, lang);
              return (
                <div key={i} className="grid grid-cols-12 gap-4 py-6 border-b" style={{ borderColor: HAIRLINE }}>
                  <span className="col-span-3 md:col-span-2 font-mono text-[14px] uppercase tracking-wider text-white">{fs.month} {fs.day}</span>
                  <span className="col-span-9 md:col-span-5 text-[15px] text-white">{s.location}</span>
                  <span className="col-span-12 md:col-span-3 text-[13px] text-white/75 italic font-mono">
                    {s.doors_open && `Doors ${s.doors_open}`}
                    {s.show_starts && ` · Show ${s.show_starts}`}
                    {s.organizer && <span className="not-italic">{` · ${s.organizer}`}</span>}
                  </span>
                  <span className="col-span-12 md:col-span-2 text-right text-[12px] font-mono">
                    {s.registration_link ? (
                      <a href={s.registration_link} target="_blank" rel="noreferrer" className="underline underline-offset-4" style={{ color: ACCENT }}>
                        🎫 →
                      </a>
                    ) : (
                      <span className="text-white/55">{t.noTickets}</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </RevealSection>

      <Rule />

      {/* Contact (if any) */}
      {event.contact && Object.keys(event.contact).length > 0 && (
        <>
          <RevealSection className="px-6 md:px-10 py-16">
            <div className="mx-auto max-w-[1400px] grid gap-8 md:grid-cols-12">
              <div className="md:col-span-3"><Eyebrow>Contact</Eyebrow></div>
              <div className="md:col-span-9 grid gap-3 md:grid-cols-3">
                {Object.entries(event.contact).map(([key, c]) => (
                  <div key={key}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/55 mb-1">{c.label ?? key}</p>
                    {c.value.startsWith('http') ? (
                      <a href={c.value} target="_blank" rel="noreferrer" className="text-[14px] text-white hover:underline">{hostname(c.value)}</a>
                    ) : (
                      <a href={`mailto:${c.value}`} className="text-[14px] text-white hover:underline">{c.value}</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </RevealSection>
          <Rule />
        </>
      )}

      {/* Related */}
      <RevealSection className="px-6 md:px-10 py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] grid gap-8 md:grid-cols-12">
          <div className="md:col-span-3"><Eyebrow>{t.alsoSee}</Eyebrow></div>
          <div className="md:col-span-9 flex flex-wrap gap-x-8 gap-y-3 text-[15px]">
            <Link to="/events" className="border-b-4 pb-2 font-semibold tracking-wide hover:no-underline" style={{ borderColor: ACCENT, color: ACCENT }}>
              {t.back.replace('← ', '→ ')}
            </Link>
            <Link to={event.type === 'poetry' ? '/v2/Qiaodong' : '/v2/GraviLens'} className="border-b-4 pb-2 font-semibold tracking-wide hover:no-underline" style={{ borderColor: ACCENT, color: ACCENT }}>
              {event.type === 'poetry' ? '桥洞 / Qiaodong →' : 'GraviLens →'}
            </Link>
          </div>
        </div>
      </RevealSection>

      <Rule />
    </V2Layout>
  );
}

function hostname(url: string) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
